import {
  queryOptions,
  infiniteQueryOptions,
  keepPreviousData
} from '@tanstack/react-query';
import { ocrTemplatesApi } from './ocr-templates.api';
import type {
  OcrTemplatesListParams,
  OcrTemplateArchivedFieldsParams
} from '../types';

export const ocrTemplateKeys = {
  all: ['ocr-templates'] as const,
  lists: () => [...ocrTemplateKeys.all, 'list'] as const,
  list: (filters: OcrTemplatesListParams) =>
    [...ocrTemplateKeys.lists(), filters] as const,
  infiniteLists: () => [...ocrTemplateKeys.all, 'infinite'] as const,
  infiniteList: (filters: OcrTemplatesListParams) =>
    [...ocrTemplateKeys.infiniteLists(), filters] as const,
  details: () => [...ocrTemplateKeys.all, 'detail'] as const,
  detail: (id: number) => [...ocrTemplateKeys.details(), id] as const,
  // Nested under `all` on purpose: a custom-field sync is what fills this
  // list, and that mutation already invalidates `all`.
  archivedFields: (id: number, filters: OcrTemplateArchivedFieldsParams = {}) =>
    [...ocrTemplateKeys.all, 'archived-fields', id, filters] as const
};

export const ocrTemplateQueries = {
  list: (filters: OcrTemplatesListParams = {}) =>
    queryOptions({
      queryKey: ocrTemplateKeys.list(filters),
      queryFn: () => ocrTemplatesApi.getAll(filters),
      placeholderData: keepPreviousData
    }),

  /**
   * Page-cursor infinite variant for scroll-to-load surfaces (e.g. the
   * template pool dialog). `filters` must not include `page` — the cursor
   * owns it.
   */
  infiniteList: (filters: OcrTemplatesListParams = {}) =>
    infiniteQueryOptions({
      queryKey: ocrTemplateKeys.infiniteList(filters),
      queryFn: ({ pageParam }) =>
        ocrTemplatesApi.getAll({ ...filters, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.hasNext ? lastPage.pagination.page + 1 : undefined,
      placeholderData: keepPreviousData
    }),

  detail: (id: number) =>
    queryOptions({
      queryKey: ocrTemplateKeys.detail(id),
      queryFn: () => ocrTemplatesApi.getById(id),
      enabled: id > 0
    }),

  /** OMA-637 — fields retired from this template, newest first. */
  archivedFields: (id: number, filters: OcrTemplateArchivedFieldsParams = {}) =>
    queryOptions({
      queryKey: ocrTemplateKeys.archivedFields(id, filters),
      queryFn: () => ocrTemplatesApi.getArchivedFields(id, filters),
      enabled: id > 0,
      placeholderData: keepPreviousData
    })
};
